import {
    type AccountOwnership,
    type AccountType,
    type RelationshipStatus,
} from '../banking/BankAccount';
import { type DeepPartial } from '../common/DeepPartial';
import type { HolisticCategory } from '../holistic/HolisticAccount';
import {
    type InvestmentManagementAccountType,
    type Portfolio,
    type RiskLevel,
    type SupportedApexAccountRegistrationType,
    type TimeHorizon,
} from '../investment-management/InvestmentManagementAccount';

export enum MoneyMapCategory {
    INCOME = 'income',
    BILLS = 'bills',
    SPEND = 'spend',
    GOALS = 'goals',
    OTHER_ACCOUNTS = 'otherAccounts',
}

export enum MoneyMapAccountStatus {
    PROPOSED = 'PROPOSED',
    EXISTS = 'EXISTS',
}

export enum MoneyMapAccountType {
    INTERNAL_BANKING = 'INTERNAL_BANKING',
    EXTERNAL_BANKING = 'EXTERNAL_BANKING',
    EXTERNAL_HOLISTIC = 'EXTERNAL_HOLISTIC',
    INVESTMENT_MANAGEMENT = 'INVESTMENT_MANAGEMENT',
    APEX_INVESTMENT_MANAGEMENT = 'APEX_INVESTMENT_MANAGEMENT',
}

/**
 * Money map rule kinds. BILLS vs SPEND look like the same "monthly amount"
 * in product copy but are persisted with different routing fields — see
 * `PaycheckRoutingRuleType`. GOAL is the only type that uses min-balance
 * as a real target-balance product (e.g. build-up guardrails).
 */
export enum MoneyMapRuleType {
    BILLS = 'BILLS',
    SPEND = 'SPEND',
    GOAL = 'GOAL',
    ACCOUNT_ONLY = 'ACCOUNT_ONLY',
}

export enum MoneyMapRuleGoalType {
    BUILD_UP_TARGET_BALANCE = 'BUILD_UP_TARGET_BALANCE',
    CONTRIBUTE_INDEFINITELY = 'CONTRIBUTE_INDEFINITELY',
    DEBT_PAY_DOWN = 'DEBT_PAY_DOWN',
}

export enum MoneyMapStatus {
    DRAFT = 'DRAFT',
    PREVIEW = 'PREVIEW', // aka Published
    ACTIVE = 'ACTIVE', // aka Finalized
    INACTIVE = 'INACTIVE', // aka Archived
    DELETED = 'DELETED', // Superseded by a review-numbers save; omitted from lists
}

/**
 * Who authored a money map version. Null on rows created before authorship
 * was tracked. `createdByUserId` is set for MEMBER only.
 */
export enum MoneyMapAuthorType {
    MEMBER = 'MEMBER',
    /** Authored through Grove; admin identity is intentionally not recorded. */
    GUIDE = 'GUIDE',
    /**
     * Generated from conversational money map intake. Not attributed to
     * whoever answered. Goyo maps (robo-advisor, no investment manager)
     * render as "Created during onboarding"; other CMM maps are drafts a
     * guide reviews and render as "your guide".
     */
    CONVERSATIONAL_ONBOARDING = 'CONVERSATIONAL_ONBOARDING',
}

/**
 * Who is steering this version, and whether investment accounts may be
 * attached. Null on maps created before this was tracked, and on Grove creates
 * that did not exit robo-advisor-no-im. A later value (robo with investment
 * accounts) is expected; do not overload GUIDANCE_APPROVED or null for that.
 */
export enum MoneyMapAdvisoryMode {
    ROBO_ADVISOR_NO_IM = 'ROBO_ADVISOR_NO_IM',
    GUIDANCE_APPROVED = 'GUIDANCE_APPROVED',
}

export enum MoneyMapRuleContributionType {
    // Maps to PaycheckRoutingRuleType.PERCENTAGE with percentage of 100%
    ALL_REMAINING_FUNDS = 'ALL_REMAINING_FUNDS',
    // Maps to PaycheckRoutingRuleType.PERCENTAGE with percentage of not 100%
    PERCENTAGE_OF_REMAINING_FUNDS = 'PERCENTAGE_OF_REMAINING_FUNDS',
}

/**
 * How a money map rule's dollar amount is stored. Do not assume the field
 * name matches how income split executes.
 *
 * Grove always writes BILLS as MINIMUM_BALANCE + `targetBalanceCents` even
 * though the guide/member UI calls that number a monthly bills amount.
 * SPEND is PERCENTAGE (usually 100%) + `monthlyContributionMaxCents`.
 * Income-split generation then turns BILLS into a monthly contribution cap,
 * so a Core "target" of $9,753 does not mean "stop when the account hits
 * $9,753" at split time — it means "up to $9,753 this calendar month."
 * GOAL rules with BUILD_UP_TARGET_BALANCE *do* mean a real available-balance
 * stop (MINIMUM_BALANCE + targetBalanceCents), and the split keeps that.
 */
export enum PaycheckRoutingRuleType {
    FIXED = 'FIXED',
    PERCENTAGE = 'PERCENTAGE',
    MINIMUM_BALANCE = 'MINIMUM_BALANCE',
}

export const MoneyMapCategoryToMoneyMapRuleType: Record<
    MoneyMapCategory,
    MoneyMapRuleType | null
> = {
    [MoneyMapCategory.BILLS]: MoneyMapRuleType.BILLS,
    [MoneyMapCategory.SPEND]: MoneyMapRuleType.SPEND,
    [MoneyMapCategory.GOALS]: MoneyMapRuleType.GOAL,
    [MoneyMapCategory.OTHER_ACCOUNTS]: MoneyMapRuleType.ACCOUNT_ONLY,
    [MoneyMapCategory.INCOME]: null,
};

export interface BaseMoneyMapAccountCreateParameters {
    name: string;
    ownership: AccountOwnership;
    primaryUserId?: string;
}

export interface MoneyMapCashAccountCreateParameters extends BaseMoneyMapAccountCreateParameters {
    category: HolisticCategory;
    virtualCard: boolean;
    physicalCard: boolean;
    icon: string;
    color: string;
}

export interface MoneyMapIMAccountCreateParameters extends BaseMoneyMapAccountCreateParameters {
    accountType: InvestmentManagementAccountType;
    portfolio: Portfolio;
    riskLevel: RiskLevel;
}

export type MoneyMapApexBackdoorRothRole = 'TRADITIONAL' | 'ROTH';

export interface MoneyMapApexAccountCreateParameters extends BaseMoneyMapAccountCreateParameters {
    /** May be non–go-flow types when the account was enrolled outside money-map (e.g. paper). */
    apexAccountType: SupportedApexAccountRegistrationType;
    /** Omitted when `backdoorRothRole` is TRADITIONAL (pass-through Trad IRA). */
    apexPortfolio?: Portfolio;
    apexRiskLevel?: RiskLevel;
    apexTimeHorizon?: TimeHorizon;
    icon: string;
    color: string;
    backdoorRothRole?: MoneyMapApexBackdoorRothRole;
}

export type MoneyMapAccountCreateParams =
    | MoneyMapCashAccountCreateParameters
    | MoneyMapIMAccountCreateParameters
    | MoneyMapApexAccountCreateParameters;

export interface MoneyMapAccountUpsertDto {
    id?: string;
    type: MoneyMapAccountType;
    membershipId: string;
    bankAccountId?: string | null;
    externalAccountId?: string | null;
    quilttAccountId?: string | null;
    moneyMapAccountIdForTransfer?: string | null;
    moneyMapAccountForTransfer?: MoneyMapAccountUpsertDto;
    /** Set on the Trad IRA row; points at the paired Roth money map account. */
    backdoorRothLinkedMoneyMapAccountId?: string | null;
    backdoorRothLinkedMoneyMapAccount?: MoneyMapAccountUpsertDto;
    status: MoneyMapAccountStatus;
    createParameters?: MoneyMapAccountCreateParams;
}

export interface MoneyMapAccount extends MoneyMapAccountUpsertDto {
    id: string;
    createdAt: string;
    updatedAt: string | null;
}

export interface MoneyMapNewFruitfulAccount extends MoneyMapAccount {
    createParameters?: MoneyMapCashAccountCreateParameters;
}

export interface MoneyMapNewApexAccount extends MoneyMapAccount {
    createParameters?: MoneyMapApexAccountCreateParameters;
    derived: {
        apexAgreements: [
            {
                agreementType:
                    | 'SOLO_BROKERAGE'
                    | 'JOINT_BROKERAGE'
                    | 'IRA'
                    | 'APEX_PRIVACY_NOTICE';
                friendlyName: string;
                agreementUri: string;
            },
        ];
    };
}

export interface MoneyMapBaseAccountDetails {
    institutionName: string;
    name: string;
    primaryUserId: string;
}

export interface MoneyMapBankAccountDetails extends MoneyMapBaseAccountDetails {
    accountType: AccountType;
    accountNumberMasked: string;
    icon?: string;
    color?: string;
    jointUserId?: string;
    jointUserRelationshipStatus?: RelationshipStatus;
    holisticCategory?: HolisticCategory;
    hasVirtualCard: boolean;
    hasPhysicalCard: boolean;
}

export interface MoneyMapIMAccountDetails extends MoneyMapBaseAccountDetails {
    accountNumberMasked?: string;
    accountType?: InvestmentManagementAccountType;
    portfolio?: Portfolio;
    riskLevel?: RiskLevel;
}

export interface MoneyMapApexAccountDetails extends MoneyMapBaseAccountDetails {
    accountNumberMasked?: string;
    /** Full Apex account number when the account is open. */
    apexAccountNumber?: string | null;
    apexAccountType?: SupportedApexAccountRegistrationType;
    apexPortfolio?: Portfolio;
    apexRiskLevel?: RiskLevel;
    apexTimeHorizon?: TimeHorizon;
    icon?: string;
    color?: string;
    backdoorRothRole?: MoneyMapApexBackdoorRothRole | null;
    backdoorRothPairedApexAccountId?: string | null;
}

export interface MoneyMapSyncteraExternalAccountDetails extends MoneyMapBaseAccountDetails {
    accountType: string;
    accountNumberMasked: string;
    logo?: string;
    syncteraAccountId: string;
}

export interface MoneyMapQuilttAccountDetails extends MoneyMapBaseAccountDetails {
    accountType: string;
    accountNumberMasked?: string;
    holisticCategory: HolisticCategory;
    logo?: string;
    minimumPayment?: number | null;
    interestRate?: number | null;
    taxonomy: string[];
}

export interface MoneyMapAccountWithAccountDetails extends MoneyMapAccount {
    accountDetails:
        | MoneyMapBankAccountDetails
        | MoneyMapIMAccountDetails
        | MoneyMapApexAccountDetails
        | MoneyMapSyncteraExternalAccountDetails
        | MoneyMapQuilttAccountDetails;
    moneyMapAccountForTransfer?: MoneyMapAccountWithAccountDetails;
    backdoorRothLinkedMoneyMapAccount?: MoneyMapAccountWithAccountDetails;
    hasRetirementContributionLimit?: boolean;
}

export interface MoneyMapUpsertDto {
    id?: string;
    membershipId: string;
    title: string;
    status: MoneyMapStatus;
    monthlyIncomeCents: number;
    incomeMoneyMapAccountId?: string;
    incomeMoneyMapAccount?: MoneyMapAccountUpsertDto;
    moneyMapRules?: MoneyMapRuleUpsertDto[];
    moneyMapRuleGroups?: MoneyMapRuleGroupUpsertDto[];
}

export interface MoneyMapUpdateStatusDto {
    status: MoneyMapStatus;
}

export interface MoneyMap extends Omit<
    MoneyMapUpsertDto,
    'moneyMapRules' | 'moneyMapRuleGroups' | 'incomeMoneyMapAccount'
> {
    id: string;
    incomeMoneyMapAccountId: string;
    incomeMoneyMapAccount?: MoneyMapAccount;
    createdAt: string;
    updatedAt: string | null;
    /**
     * Who authored this version. Null on maps created before authorship was
     * tracked.
     */
    createdByType?: MoneyMapAuthorType | null;
    /**
     * Set only when `createdByType` is MEMBER. Null for GUIDE /
     * CONVERSATIONAL_ONBOARDING by design.
     */
    createdByUserId?: string | null;
    isAIGenerated?: boolean; // Only set on the return object from the llm-service call (is not persisted to the backend entity)
    eligibleForMemberEdit?: boolean;
    /**
     * Server-owned. Null is legacy. Member edits copy it. A guide edit or
     * duplicate is the only exit from ROBO_ADVISOR_NO_IM.
     */
    advisoryMode?: MoneyMapAdvisoryMode | null;
}

/** Request body for `POST /api/money-map/update` (member edit). */
export interface MoneyMapMemberUpdateRequest {
    moneyMapId: string;
    monthlyIncomeCents: number;
    ruleGroups: Array<{
        id: string;
        name: string;
        description?: string | null;
        priority: number;
        percentage: number;
    }>;
    rules: Array<{
        id: string;
        moneyMapAccountId: string;
        moneyMapRuleGroupId: string | null;
        type: MoneyMapRuleType;
        goalType?: MoneyMapRuleGoalType | null;
        description: string;
        priority: number;
        routingRuleType?: PaycheckRoutingRuleType | null;
        percentage: number | null;
        fixedAmountCents: number | null;
        targetBalanceCents: number | null;
        monthlyContributionMaxCents: number | null;
        yearlyContributionMaxCents: number | null;
    }>;
    incomeSplitSettings: {
        onlyExecuteOnPaychecks: boolean;
        accountIds: string[];
    };
    /**
     * `updatedAt` of the money map as last loaded. When provided, the backend
     * rejects with 409 if the map changed since (e.g. a guide finalized a new
     * version while the member was editing).
     */
    expectedUpdatedAt?: string;
}

export interface MoneyMapRuleGroupUpsertDto {
    id?: string;
    moneyMapId: string;
    priority: number;
    percentage: number;
    name: string;
    description?: string;
    moneyMapRules?: MoneyMapRuleUpsertDto[];
}

export interface MoneyMapRuleGroup extends MoneyMapRuleGroupUpsertDto {
    id: string;
    createdAt: string;
    updatedAt: string | null;
}

export interface MoneyMapRuleUpsertDto {
    id?: string;
    originalId?: string;
    moneyMapId?: string;
    moneyMapAccountId?: string;
    moneyMapAccount?: MoneyMapAccountUpsertDto;
    moneyMapRuleGroupId?: string;
    type: MoneyMapRuleType;
    goalType?: MoneyMapRuleGoalType;
    description: string;
    priority: number;
    routingRuleType?: PaycheckRoutingRuleType | null;
    fixedAmountCents?: number | null;
    percentage?: number | null;
    /**
     * For BILLS this is the monthly bills figure, not an account target.
     * For GOAL/MINIMUM_BALANCE it is a real available-balance stop.
     * See `PaycheckRoutingRuleType`.
     */
    targetBalanceCents?: number | null;
    /** Monthly cap. Spend (and income-split bills) live here; Grove BILLS rows do not. */
    monthlyContributionMaxCents?: number | null;
    yearlyContributionMaxCents?: number | null;
}

export interface MoneyMapRule extends Omit<
    MoneyMapRuleUpsertDto,
    'moneyMapAccount' | 'goalType' | 'routingRuleType'
> {
    id: string;
    moneyMapId: string;
    moneyMapAccountId: string;
    goalType?: MoneyMapRuleGoalType | null;
    routingRuleType?: PaycheckRoutingRuleType | null;
    createdAt: string;
    updatedAt: string | null;
}

export interface MoneyMapRuleWithAccount extends MoneyMapRule {
    moneyMapAccount: MoneyMapAccount;
}

export interface MoneyMapRuleWithAccountDetails extends MoneyMapRule {
    moneyMapAccount: MoneyMapAccountWithAccountDetails;
}

export interface MoneyMapWithRulesAndAccounts extends MoneyMap {
    moneyMapRules: MoneyMapRuleWithAccount[];
    moneyMapRuleGroups: MoneyMapRuleGroup[];
}

export enum IncomeSplitConfigCreatedVia {
    MEMBER_MONEY_MAP_EDIT = 'MEMBER_MONEY_MAP_EDIT',
    MONEY_MAP_PROPOSAL_ACCEPTED = 'MONEY_MAP_PROPOSAL_ACCEPTED',
    INCOME_SPLIT_EDITOR = 'INCOME_SPLIT_EDITOR',
}

/**
 * The membership's current live split, for Grove to badge maps against.
 * Null `createdVia` is a paycheck/v0 fallback (no income-split row).
 */
export interface CurrentIncomeSplitSnapshot {
    id: string;
    moneyMapId: string | null;
    status: string;
    createdVia: IncomeSplitConfigCreatedVia | null;
}

export type MoneyMapWithGroveSplitContext = MoneyMapWithRulesAndAccounts & {
    isCurrentIncomeSplitSource: boolean;
};

export interface MoneyMapsForMembershipAdmin {
    moneyMaps: MoneyMapWithGroveSplitContext[];
    currentIncomeSplit: CurrentIncomeSplitSnapshot | null;
}

// Typings used to handle rules & groups in one single sorted array
export type GroupWithEntityType = DeepPartial<MoneyMapRuleGroup> & {
    entityType: 'group';
};
export type RuleWithEntityType = DeepPartial<MoneyMapRuleWithAccountDetails> & {
    entityType: 'rule';
};
export type RuleOrGroup = RuleWithEntityType | GroupWithEntityType;

export const NEW_CASH_ACCOUNT_OPTION = {
    label: 'New Fruitful Cash account',
    value: 'New Fruitful Cash account',
};
export const NEW_IM_ACCOUNT_OPTION = {
    label: 'New IM account',
    value: 'New IM account',
};
export const NEW_APEX_IM_ACCOUNT_OPTION = {
    label: 'New Apex IM account',
    value: 'New Apex IM account',
};
